import type { CommonData } from '@/components/blockEditor/blockEditor.types';
import { CURRENT_SCHEMA_VERSION } from '@/components/blockEditor/blockEditor.types';

export function getDefaultSlatOverviewPageOneData(): CommonData {
  return {
    version: CURRENT_SCHEMA_VERSION,
    blocks: [
      {
        id: 'description',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'slat-overview-heading',
              type: 'EducationalSlideHeading',
              props: {
                title: 'What is a SLAT?',
                variant: 'h3',
              },
            },
            {
              id: 'slat-overview-body',
              type: 'EditableEducationalSlideBody',
              props: {
                text: "A Spousal Lifetime Access Trust (SLAT) is an irrevocable trust created by a grantor for the benefit of their spouse and potentially other family members.\n\n**A SLAT allows the beneficiary spouse to access the income and/or principal of the trust during their lifetime, while removing the assets from the grantor's estate, thus offering potential estate tax benefits.** SLATs are beneficial in times of financial certainty, when both spouses do not foresee needing immediate access to the full amount of their assets, but still want the possibility of some access through the beneficiary spouse.",
              },
            },
          ],
        },
      },
      {
        id: 'benefits',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'slat-overview-page-one-columns',
              type: 'TwoColumnLayout',
              props: {
                leftWidth: '50%',
                rightWidth: '50%',
                columnGap: 2,
                showDivider: true,
              },
              children: {
                leftColumn: [
                  {
                    id: 'slat-overview-benefits-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Potential benefits',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'slat-benefit-estate',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Estate tax reduction',
                      description:
                        "Assets transferred to the SLAT are removed from the grantor's estate, which can significantly reduce future estate tax liability.",
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'slat-benefit-flexibility',
                    type: 'PresentationListItem',
                    props: {
                      iconColor: 'PRIMARY',
                      title: 'Flexibility',
                      description:
                        'Although the trust is irrevocable, the beneficiary spouse can access the trust’s income and/or principal if needed, providing financial flexibility.',
                      iconType: 'check',
                    },
                  },
                  {
                    id: 'slat-benefit-wealth',
                    type: 'PresentationListItem',
                    props: {
                      iconColor: 'PRIMARY',
                      title: 'Wealth preservation',
                      description:
                        'Assets within the SLAT are protected from creditors and can be managed according to the grantor’s wishes, ensuring they are preserved for future generations.',
                      iconType: 'check',
                    },
                  },
                  {
                    id: 'slat-benefit-tax-free-growth',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Income tax-free growth',
                      description:
                        'As a grantor trust, a SLAT grows unencumbered by income taxes. Taxes generated by the trust are paid by the grantor using assets outside of the trust, further reducing potential estate tax exposure.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                ],
                rightColumn: [
                  {
                    id: 'slat-overview-drawbacks-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Potential drawbacks',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'slat-drawback-irrevocability',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Irrevocability',
                      description:
                        'Once the SLAT is established, the grantor cannot make changes or dissolve the trust. This lack of flexibility can be limiting if financial or marital circumstances change.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'slat-drawback-marital-status',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Dependency on marital status',
                      description:
                        'If the couple divorces, access to the SLAT could be severely limited or lost entirely, posing a significant risk.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'slat-drawback-tax-complications',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Potential tax complications',
                      description:
                        'While the trust itself may offer tax benefits, there can be complex implications for income taxes or gift taxes, requiring careful planning and management.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'slat-drawback-complexity',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Complexity & costs',
                      description:
                        'Creating and maintaining a SLAT involves legal and administrative expenses, and the structure may require ongoing professional management.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    ],
  };
}
